/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import styled from "styled-components";

const CaptchaContainer = styled.div`
  margin: 8px 0;
`;

function loadSBCaptcha(scriptUrl) {
  return new Promise(function (resolve) {
    if (window.SBCaptcha) { resolve(window.SBCaptcha); return; }
    if (document.querySelector('script[src="' + scriptUrl + '"]')) {
      // Script tag exists but SBCaptcha not yet set — wait for it
      var check = setInterval(function () {
        if (window.SBCaptcha) { clearInterval(check); resolve(window.SBCaptcha); }
      }, 20);
      return;
    }
    var s    = document.createElement("script");
    s.src    = scriptUrl;
    s.onload = function () { resolve(window.SBCaptcha || null); };
    s.onerror = function () { resolve(null); };
    document.head.appendChild(s);
  });
}

/**
 * Renders the captcha widget for the current provider via SBCaptcha global.
 * Exposes getToken() via ref for form submission.
 *
 * Props:
 *   challenge  — object from /public/captcha/challenge
 *
 * Ref methods:
 *   getToken() → Promise<string|null>
 */
const CaptchaWidget = forwardRef(function CaptchaWidget({ challenge }, ref) {
  const containerRef = useRef(null);
  const widgetRef    = useRef(null);
  const tokenRef     = useRef(null);

  useImperativeHandle(ref, function () {
    return {
      getToken: function () {
        if (!widgetRef.current) return Promise.resolve(null);
        // imagecaptcha has no onToken event — read the input value at call time
        if (challenge && challenge.provider === 'imagecaptcha') {
          return widgetRef.current.getToken();
        }
        // All other providers: token captured via onToken callback, return immediately
        return Promise.resolve(tokenRef.current);
      },
    };
  }, [challenge]);

  useEffect(
    function () {
      if (!challenge || !containerRef.current) return;
      var cancelled = false;
      tokenRef.current = null;

      loadSBCaptcha(challenge.widgetUrl).then(function (SBCaptcha) {
        if (cancelled || !SBCaptcha || !containerRef.current) return;

        widgetRef.current = new SBCaptcha({
          container: containerRef.current,
          challenge: challenge,
          onToken: function (token) { tokenRef.current = token; },
        });
      });

      return function () {
        cancelled = true;
        if (widgetRef.current) { widgetRef.current.reset(); widgetRef.current = null; }
      };
    },
    [challenge]
  );

  if (!challenge) return null;

  return <CaptchaContainer ref={containerRef} />;
});

export default CaptchaWidget;
